import { addDays, format, parse } from "date-fns";
import { END_DATE, START_DATE } from "src/constants/filter";
import { FilterInput, FilterValue, FilterValues } from "src/types/report";

// Set to 0 to disable offset, or remove calls to applyFilterDateOffset entirely.
const FILTER_DATE_OFFSET_DAYS: number = 0;

const DATE_FORMAT = "yyyy-MM-dd";

// start_date/end_date are never multi-select, but FilterValues permits an array
// for any key — this narrows before the date math instead of casting, and keeps
// the original truthiness check (an unset "" date is left alone).
const isDateValue = (
  value: FilterValue | undefined,
): value is string | number => !Array.isArray(value) && Boolean(value);

export const applyFilterDateOffset = (
  fields: FilterValues,
): FilterValues => {
  if (FILTER_DATE_OFFSET_DAYS === 0) return fields;
  const shift = (val: string | number) =>
    format(
      addDays(
        parse(String(val), DATE_FORMAT, new Date()),
        FILTER_DATE_OFFSET_DAYS,
      ),
      DATE_FORMAT,
    );
  return {
    ...fields,
    ...(isDateValue(fields.start_date)
      ? { start_date: shift(fields.start_date) }
      : {}),
    ...(isDateValue(fields.end_date)
      ? { end_date: shift(fields.end_date) }
      : {}),
  };
};

/**
 * Filter keys the DASHBOARD renders as a multi-select, and so submits as an
 * array. Dashboard-only on purpose: the dashboard backend reads `drug_name` as
 * one product or several (drug_filter.py), while the reports backend still
 * matches a product with `=` — every seeded report product filter is
 * `filter_type: "exact"`, which binds the value to a single SQL parameter — so
 * a list there would break the query. ReportPage therefore does not pass this.
 */
export const MULTI_SELECT_FILTER_KEYS: readonly string[] = ["drug_name"];

/**
 * The values of the "All Products" option, which means "no product filter".
 * Two of them because the option is seeded differently per tenant: the
 * dynamically resolved product list uses "" (analytics_backend config_service
 * `_resolve_static_options`) and the Exparel seed uses "all". The backend
 * treats both, and an empty array, identically.
 */
const ALL_OPTION_VALUES: ReadonlySet<FilterValue> = new Set(["", "all"]);

export const isAllFilterOptionValue = (value: FilterValue): boolean =>
  ALL_OPTION_VALUES.has(value);

/**
 * A filter value in the array form a multi-select works with.
 *
 * The "All Products" entries are dropped rather than carried along: an empty
 * array already says "every product" to the backend, and keeping "all" in a
 * list would cancel the other selections server-side while "" would be ignored
 * — the same click behaving differently per tenant.
 */
export const toMultiFilterValue = (
  value: FilterValue | undefined,
): Array<string | number> => {
  const items =
    value === undefined ? [] : Array.isArray(value) ? value : [value];
  return items.filter((item) => !isAllFilterOptionValue(item));
};

/**
 * Product labels masked as "Product 1", "Product 2", … for demo accounts,
 * keyed by the value that is actually submitted (which stays the real drug
 * name, so the payload is unaffected).
 *
 * Shared by the Products dropdown and the data-context bar deliberately: the
 * bar prints the same selection back as a sentence, and a demo account that
 * picked "Product 2" must not read the real drug name in the line underneath.
 * One numbering, one place.
 *
 * "All Products" is excluded so it keeps its own label and doesn't consume a
 * number — it is a scope, not a product. Non-demo accounts get an empty map
 * and callers fall through to the real label.
 */
export const maskedProductLabels = (
  options: readonly { value: string | number; label: string }[],
  isDemoUser: boolean,
): Map<string | number, string> =>
  new Map(
    isDemoUser
      ? options
          .filter((option) => !isAllFilterOptionValue(option.value))
          .map(
            (option, index) =>
              [option.value, `Product ${index + 1}`] as [string | number, string],
          )
      : [],
  );

/**
 * Forces every multi-select key into the array form, so the payload shape
 * doesn't depend on whether the user has touched the control. Needed because a
 * filter is seeded from its `default_value` (a scalar) by getInitialValues
 * below, and a value carried over from the filter store may predate the
 * multi-select.
 */
export const normalizeMultiSelectFilters = (
  fields: FilterValues,
  multiKeys: readonly string[],
): FilterValues => {
  const next: FilterValues = { ...fields };
  multiKeys.forEach((key) => {
    if (key in next) next[key] = toMultiFilterValue(next[key]);
  });
  return next;
};

export const getInitialValues = (fields: FilterInput[]) =>
  fields.reduce<FilterValues>((acc, field) => {
    if (field.key === "start_date") {
      acc[field.key] = START_DATE;
      return acc;
    }

    if (field.key === "end_date") {
      acc[field.key] = END_DATE;
      return acc;
    }

    if (
      field.control_type === "tabs" &&
      field.static_options &&
      field.static_options.length > 0
    ) {
      acc[field.key] = field.default_value ?? "";
      return acc;
    }

    acc[field.key] = field.default_value ?? "";
    return acc;
  }, {});
